
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BuyNewHomeInputs, BuyNewHomeCalculationResults, PayoffInputs, PayoffCalculationResults, PayoffMonthlyData, BuyNewHomeMonthlyData } from '../types';

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};
const formatDate = (date: string) => new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
const formatCompact = (value: number) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumSignificantDigits: 3 }).format(value);


const addHeader = (doc: jsPDF, title: string) => {
    doc.setFontSize(16);
    doc.setTextColor('#33424e'); // bp-slate
    doc.text(title, 40, 50);
};

const addFooter = (doc: jsPDF) => {
    const pageCount = doc.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Report Generated: ${new Date().toLocaleDateString()}`, 40, pageHeight - 20);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 40, pageHeight - 20, { align: 'right' });
    }
};

const drawSectionTitle = (doc: jsPDF, title: string, yPos: number) => {
    doc.setFontSize(12);
    doc.setTextColor('#33424e');
    doc.text(title, 40, yPos);
    return yPos + 15;
};


// --- Payoff vs. Invest PDF ---
export const generatePayoffPdf = (inputs: PayoffInputs, results: PayoffCalculationResults) => {
    const doc = new jsPDF('p', 'pt', 'a4');
    const { summary, monthlyData } = results;
    const reportTitle = "Pay Off Mortgage vs. Invest: Financial Report";

    addHeader(doc, reportTitle);
    let finalY = 70;

    // --- Summary Section ---
    finalY = drawSectionTitle(doc, "Key Projections", finalY);
    
    const isInvestingBetter = summary.finalInvestNetWorth > summary.finalPayoffNetWorth;
    const difference = Math.abs(summary.finalInvestNetWorth - summary.finalPayoffNetWorth);
    const higherStrategy = isInvestingBetter ? 'Investing the extra cash' : 'Paying off your mortgage early';

    const summaryText = `Based on your inputs, ${higherStrategy} is projected to result in a higher net worth by ${formatCurrency(difference)}.`;
    const splitText = doc.splitTextToSize(summaryText, doc.internal.pageSize.width - 80);
    doc.setFontSize(10);
    doc.text(splitText, 40, finalY);
    finalY += doc.getTextDimensions(splitText).h + 10;

    const summaryData = [
      ['Strategy', 'Final Net Worth', 'Final Home Equity', 'Final Investment Value (After-Tax)'],
      ['Pay Off Early', formatCurrency(summary.finalPayoffNetWorth), formatCurrency(monthlyData[monthlyData.length-1].payoffHomeEquity), formatCurrency(summary.payoffInvestmentDetails.finalValueAfterTax)],
      ['Invest', formatCurrency(summary.finalInvestNetWorth), formatCurrency(monthlyData[monthlyData.length-1].investHomeEquity), formatCurrency(summary.investInvestmentDetails.finalValueAfterTax)],
    ];
    (autoTable as any)(doc, {
      startY: finalY,
      head: [summaryData[0]],
      body: summaryData.slice(1),
      theme: 'grid',
      headStyles: { fillColor: '#3387ae' }, // bp-dark-blue
    });
    finalY = doc.lastAutoTable.finalY + 20;

    // --- Inputs Section ---
    finalY = drawSectionTitle(doc, "Your Assumptions", finalY);

    const inputsData = [
        { label: 'Original Loan Amount', value: formatCurrency(inputs.originalLoanAmount) },
        { label: 'Interest Rate', value: `${inputs.interestRate}%` },
        { label: 'Origination Date', value: formatDate(inputs.originationDate) },
        { label: 'Loan Term', value: `${inputs.loanTerm} Years` },
        { label: 'Current Home Value', value: formatCurrency(inputs.homeValue) },
        { label: 'Annual Appreciation', value: `${inputs.appreciation}%` },
        { label: 'Extra Monthly Payment', value: formatCurrency(inputs.extraPayment) },
        { label: 'Filing Status', value: inputs.filingStatus },
        { label: 'Marginal Tax Rate', value: `${inputs.marginalTaxRate}%` },
        { label: 'Other Deductions', value: formatCurrency(inputs.otherItemizedDeductions) },
        { label: 'Initial Return Rate', value: `${inputs.initialReturnRate}% for ${inputs.initialReturnPeriod} years` },
        { label: 'Long-Term Return Rate', value: `${inputs.longTermReturnRate}%` },
        { label: 'Tax on Gains', value: `${inputs.taxRateOnGains}%` },
        ...(inputs.lumpSums.length > 0 ? [{ label: 'Lump Sum Payments', value: inputs.lumpSums.map(l => `${formatCurrency(l.amount)} in Year ${l.yearsFromNow}`).join(', ') }] : []),
    ];
    (autoTable as any)(doc, {
        startY: finalY,
        body: inputsData.map(d => [d.label, d.value]),
        theme: 'plain',
        styles: { cellPadding: 2, fontSize: 9 }
    });
    finalY = doc.lastAutoTable.finalY;

    // --- Yearly Data Table ---
    const yearlyDataMap = new Map<number, PayoffMonthlyData>();
    monthlyData.forEach(row => {
        if (row.month % 12 === 0 || row.month === monthlyData.length) yearlyDataMap.set(row.year, row);
    });
    if (!yearlyDataMap.has(monthlyData[0].year) && monthlyData.length < 12) {
        yearlyDataMap.set(monthlyData[0].year, monthlyData[monthlyData.length-1]);
    }
    const yearlyData = Array.from(yearlyDataMap.values()).sort((a, b) => a.year - b.year);

    const tableBody = yearlyData.map(row => [
        row.year,
        formatCompact(row.payoffBalance),
        formatCompact(row.payoffHomeEquity),
        formatCompact(row.payoffInvestBalance),
        formatCompact(row.payoffTaxSavingsThisYear),
        formatCompact(row.payoffNetWorth),
        formatCompact(row.originalBalance),
        formatCompact(row.investHomeEquity),
        formatCompact(row.investBalance),
        formatCompact(row.taxSavingsThisYear),
        formatCompact(row.investNetWorth),
    ]);
    
    (autoTable as any)(doc, {
        startY: finalY + 20,
        head: [
            [{ content: 'Year', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } }, { content: 'Pay Off Early Scenario', colSpan: 5, styles: { halign: 'center', fillColor: '#7eb1cd', textColor: 255 } }, { content: 'Invest Scenario', colSpan: 5, styles: { halign: 'center', fillColor: '#a8d5ba', textColor: '#33424e' } }],
            ['Mortgage', 'Home Equity', 'Invested', 'Tax Savings', 'Net Worth', 'Mortgage', 'Home Equity', 'Invested', 'Tax Savings', 'Net Worth']
        ],
        body: tableBody,
        theme: 'grid',
        headStyles: { fillColor: '#33424e', textColor: 255, fontSize: 8, cellPadding: 3 },
        bodyStyles: { fontSize: 8, cellPadding: 2 },
        alternateRowStyles: { fillColor: '#f8f9fa' },
        didDrawPage: (data: any) => {
            addHeader(doc, reportTitle);
            let titleY = data.settings.startY && data.settings.startY > 70 ? data.settings.startY - 20 : 70;
            drawSectionTitle(doc, "Year-by-Year Wealth Projection", titleY);
        }
    });

    addFooter(doc);

    doc.save('Payoff_vs_Invest_Report.pdf');
};

// --- Finance vs. Pay Cash PDF ---
export const generateBuyNewHomePdf = (inputs: BuyNewHomeInputs, results: BuyNewHomeCalculationResults) => {
    const doc = new jsPDF('p', 'pt', 'a4');
    const { summary, monthlyData } = results;
    const reportTitle = "Finance vs. Pay Cash: Financial Report";
    
    addHeader(doc, reportTitle);
    let finalY = 70;

    // --- Summary Section ---
    finalY = drawSectionTitle(doc, "Key Projections", finalY);

    const isFinancingBetter = summary.financeScenarioFinalNetWorth > summary.payCashScenarioFinalNetWorth;
    const difference = Math.abs(summary.financeScenarioFinalNetWorth - summary.payCashScenarioFinalNetWorth);
    const higherStrategy = isFinancingBetter ? 'Financing your home' : 'Paying in cash';

    const summaryText = `Based on your inputs, ${higherStrategy} is projected to result in a higher net worth by ${formatCurrency(difference)} after ${Math.ceil(monthlyData.length/12)} years.`;
    const splitText = doc.splitTextToSize(summaryText, doc.internal.pageSize.width - 80);
    doc.setFontSize(10);
    doc.text(splitText, 40, finalY);
    finalY += doc.getTextDimensions(splitText).h + 10;

    const summaryData = [
      ['Strategy', 'Final Net Worth', 'Final Home Equity', 'Final Investment Value'],
      ['Finance & Invest', formatCurrency(summary.financeScenarioFinalNetWorth), formatCurrency(monthlyData[monthlyData.length-1].homeEquity), formatCurrency(monthlyData[monthlyData.length-1].investedBalanceFinance)],
      ['Pay Cash', formatCurrency(summary.payCashScenarioFinalNetWorth), formatCurrency(monthlyData[monthlyData.length-1].homeValue), formatCurrency(monthlyData[monthlyData.length-1].investedBalancePayCash)],
    ];
    (autoTable as any)(doc, {
        startY: finalY,
        head: [summaryData[0]],
        body: summaryData.slice(1),
        theme: 'grid',
        headStyles: { fillColor: '#3387ae' },
    });
    finalY = doc.lastAutoTable.finalY + 20;

    // --- Inputs Section ---
    finalY = drawSectionTitle(doc, "Your Assumptions", finalY);

    const inputsData = [
        { label: 'Purchase Price', value: formatCurrency(inputs.home.purchasePrice) },
        { label: 'Down Payment', value: `${inputs.home.downPayment}% (${formatCurrency(summary.downPaymentAmount)})` },
        { label: 'Interest Rate', value: `${inputs.home.interestRate}%` },
        { label: 'Loan Term', value: `${inputs.home.loanTerm} Years` },
        { label: 'Annual Appreciation', value: `${inputs.home.appreciation}%` },
        { label: 'Closing Costs', value: formatCurrency(inputs.home.closingCosts) },
        { label: 'Insurance Savings (Cash)', value: `${formatCurrency(inputs.home.insuranceSavings)}/yr` },
        { label: 'Filing Status', value: inputs.tax.filingStatus },
        { label: 'Marginal Tax Rate', value: `${inputs.tax.marginalTaxRate}%` },
        { label: 'Other Deductions', value: formatCurrency(inputs.tax.otherItemizedDeductions) },
        { label: 'Initial Return Rate', value: `${inputs.investment.initialReturnRate}% for ${inputs.investment.initialReturnPeriod} years` },
        { label: 'Long-Term Return Rate', value: `${inputs.investment.longTermReturnRate}%` },
        { label: 'Tax on Gains', value: `${inputs.investment.taxRateOnGains}%` },
        ...(inputs.home.lumpSums.length > 0 ? [{ label: 'Lump Sum Payments', value: inputs.home.lumpSums.map(l => `${formatCurrency(l.amount)} in Year ${l.yearsFromNow}`).join(', ') }] : []),
    ];
    (autoTable as any)(doc, {
        startY: finalY,
        body: inputsData.map(d => [d.label, d.value]),
        theme: 'plain',
        styles: { cellPadding: 2, fontSize: 9 }
    });
    finalY = doc.lastAutoTable.finalY;
    
    // --- Yearly Data Table ---
    const yearlyDataMap = new Map<number, BuyNewHomeMonthlyData>();
    monthlyData.forEach(row => {
        if (row.month % 12 === 0 || row.month === monthlyData.length) yearlyDataMap.set(row.year, row);
    });
    if (!yearlyDataMap.has(monthlyData[0].year) && monthlyData.length < 12) {
        yearlyDataMap.set(monthlyData[0].year, monthlyData[monthlyData.length-1]);
    }
    const yearlyData = Array.from(yearlyDataMap.values()).sort((a, b) => a.year - b.year);

    const tableBody = yearlyData.map(row => [
        row.year,
        formatCompact(row.mortgageBalance),
        formatCompact(row.homeEquity),
        formatCompact(row.investedBalanceFinance),
        formatCompact(row.taxSavingsThisYear),
        formatCompact(row.financeScenarioNetWorth),
        formatCompact(0),
        formatCompact(row.homeValue),
        formatCompact(row.investedBalancePayCash),
        formatCompact(0),
        formatCompact(row.payCashScenarioNetWorth),
    ]);

    (autoTable as any)(doc, {
        startY: finalY + 20,
        head: [
            [{ content: 'Year', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } }, { content: 'Finance & Invest Scenario', colSpan: 5, styles: { halign: 'center', fillColor: '#7eb1cd', textColor: 255 } }, { content: 'Pay Cash Scenario', colSpan: 5, styles: { halign: 'center', fillColor: '#a8d5ba', textColor: '#33424e' } }],
            ['Mortgage', 'Home Equity', 'Invested', 'Tax Savings', 'Net Worth', 'Mortgage', 'Home Equity', 'Invested', 'Tax Savings', 'Net Worth']
        ],
        body: tableBody,
        theme: 'grid',
        headStyles: { fillColor: '#33424e', textColor: 255, fontSize: 8, cellPadding: 3 },
        bodyStyles: { fontSize: 8, cellPadding: 2 },
        alternateRowStyles: { fillColor: '#f8f9fa' },
        didDrawPage: (data: any) => {
            addHeader(doc, reportTitle);
            let titleY = data.settings.startY && data.settings.startY > 70 ? data.settings.startY - 20 : 70;
            drawSectionTitle(doc, "Year-by-Year Breakdown", titleY);
        }
    });
    
    addFooter(doc);
    doc.save('Finance_vs_Cash_Report.pdf');
};
